import React, { useState, useEffect, useCallback } from 'react';

const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwm5h-p_EJG01ckL9oYTl5Uu13d7pJtfEJDQu-o0a5z11HsycEKsyyo3CV_WpypNlE/exec';

// ─── THEME STYLES (AGGRESSIVE GLOBAL DARK MODE) ─────────────────────────────
const themeStyles = `
  /* Fallback / Light Mode Variables */
  :root, body, html, .nova-emp-wrapper {
    --bg-page: #f1f5f9;
    --bg-card: #ffffff;
    --text-main: #111827;
    --text-secondary: #6b7280;
    --border-light: #e5e7eb;
    --border-divider: #f1f5f9;
    --header-bg: #ffffff;
    --header-border: #1e3a8a;
    --logo-bg: linear-gradient(135deg, #1e3a8a, #2563eb);
    --input-bg: #ffffff;
    --stat-bg: #f8fafc;
    --modal-overlay: rgba(0,0,0,0.4);

    --badge-active-bg: #EAF3DE;
    --badge-active-text: #3B6D11;
    --badge-inactive-bg: #F1EFE8;
    --badge-inactive-text: #5F5E5A;
    --badge-rejected-bg: #FCEBEB;
    --badge-rejected-text: #A32D2D;
    --badge-activated-bg: #E6F1FB;
    --badge-activated-text: #185FA5;
    --badge-hold-bg: #FAEEDA;
    --badge-hold-text: #854F0B;

    --av-0-bg: #E6F1FB; --av-0-text: #185FA5;
    --av-1-bg: #EAF3DE; --av-1-text: #3B6D11;
    --av-2-bg: #EEEDFE; --av-2-text: #3C3489;
    --av-3-bg: #FAEEDA; --av-3-text: #854F0B;
    --av-4-bg: #E1F5EE; --av-4-text: #0F6E56;
    --av-5-bg: #FAECE7; --av-5-text: #993C1D;

    --toggle-on: #639922;
    --toggle-off: #D3D1C7;
  }

  /* Extreme Aggressive Dark Mode Selectors (Catches everything) */
  html.dark, html.dark-mode, html[data-theme='dark'], html[data-bs-theme='dark'],
  body.dark, body.dark-mode, body[data-theme='dark'], body[data-bs-theme='dark'],
  .dark, .dark-mode, [data-theme='dark'], [data-bs-theme='dark'],
  .dark .nova-emp-wrapper, .dark-mode .nova-emp-wrapper, [data-theme='dark'] .nova-emp-wrapper, [data-bs-theme='dark'] .nova-emp-wrapper,
  .nova-emp-wrapper.dark, .nova-emp-wrapper[data-theme='dark'] {
    --bg-page: #0b1437 !important;
    --bg-card: #111c44 !important;
    --text-main: #ffffff !important;
    --text-secondary: #a3aed0 !important;
    --border-light: #2b3674 !important;
    --border-divider: #1e293b !important;
    --header-bg: #111c44 !important;
    --header-border: #38bdf8 !important;
    --logo-bg: linear-gradient(135deg, #38bdf8, #2563eb) !important;
    --input-bg: #0b1437 !important;
    --stat-bg: #0f172a !important;
    --modal-overlay: rgba(0,0,0,0.7) !important;

    /* Badges - dark mode adjusted */
    --badge-active-bg: rgba(34, 197, 94, 0.15) !important;
    --badge-active-text: #4ade80 !important;
    --badge-inactive-bg: rgba(148, 163, 184, 0.15) !important;
    --badge-inactive-text: #94a3b8 !important;
    --badge-rejected-bg: rgba(248, 113, 113, 0.15) !important;
    --badge-rejected-text: #f87171 !important;
    --badge-activated-bg: rgba(56, 189, 248, 0.15) !important;
    --badge-activated-text: #38bdf8 !important;
    --badge-hold-bg: rgba(245, 158, 11, 0.15) !important;
    --badge-hold-text: #fbbf24 !important;

    /* Avatars - dark mode adjusted */
    --av-0-bg: rgba(56, 189, 248, 0.15) !important; --av-0-text: #38bdf8 !important;
    --av-1-bg: rgba(74, 222, 128, 0.15) !important; --av-1-text: #4ade80 !important;
    --av-2-bg: rgba(167, 139, 250, 0.15) !important; --av-2-text: #a78bfa !important;
    --av-3-bg: rgba(251, 191, 36, 0.15) !important; --av-3-text: #fbbf24 !important;
    --av-4-bg: rgba(45, 212, 191, 0.15) !important; --av-4-text: #2dd4bf !important;
    --av-5-bg: rgba(248, 113, 113, 0.15) !important; --av-5-text: #f87171 !important;

    --toggle-on: #4ade80 !important;
    --toggle-off: #475569 !important;
  }

  .nova-emp-wrapper {
    background-color: var(--bg-page);
    color: var(--text-main);
    min-height: 100vh;
    font-family: 'Segoe UI', system-ui, sans-serif;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .nova-emp-wrapper * {
    transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
  }

  .nova-emp-wrapper input, .nova-emp-wrapper select {
    color-scheme: light dark;
  }
`;

// ─── Helpers ────────────────────────────────────────────────────────────────

const getInitials = (name = '') => {
  if (!name) return '?';
  return String(name).trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
};

const avatarColors = [
  { bg: 'var(--av-0-bg)', color: 'var(--av-0-text)' },
  { bg: 'var(--av-1-bg)', color: 'var(--av-1-text)' },
  { bg: 'var(--av-2-bg)', color: 'var(--av-2-text)' },
  { bg: 'var(--av-3-bg)', color: 'var(--av-3-text)' },
  { bg: 'var(--av-4-bg)', color: 'var(--av-4-text)' },
  { bg: 'var(--av-5-bg)', color: 'var(--av-5-text)' },
];

const getAvatarColor = (name = '') => {
  if (!name) return avatarColors[0];
  const charCode = String(name).charCodeAt(0);
  return avatarColors[charCode % avatarColors.length] || avatarColors[0];
};

const statusBadge = (isActive) => ({
  label: isActive ? 'Active' : 'Inactive',
  bg: isActive ? 'var(--badge-active-bg)' : 'var(--badge-inactive-bg)',
  color: isActive ? 'var(--badge-active-text)' : 'var(--badge-inactive-text)',
});

const approvalBadge = (val) => {
  if (!val) return null;
  const map = {
    Approved: { bg: 'var(--badge-active-bg)', color: 'var(--badge-active-text)' },
    Rejected: { bg: 'var(--badge-rejected-bg)', color: 'var(--badge-rejected-text)' },
    Activated: { bg: 'var(--badge-activated-bg)', color: 'var(--badge-activated-text)' },
    'On Hold': { bg: 'var(--badge-hold-bg)', color: 'var(--badge-hold-text)' },
  };
  return map[val] || { bg: 'var(--badge-inactive-bg)', color: 'var(--badge-inactive-text)' };
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const S = {
  page: {
    height: '100vh',
    overflowY: 'auto',
    background: 'var(--bg-page)',
    padding: '20px 16px 40px',
    boxSizing: 'border-box',
  },
  wrap: { maxWidth: 960, margin: '0 auto' },
  header: {
    background: 'var(--header-bg)',
    borderRadius: '12px 12px 0 0',
    padding: '16px 22px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    borderBottom: '3px solid var(--header-border)',
    flexWrap: 'wrap',
  },
  logo: {
    width: 46, height: 46, borderRadius: 10,
    background: 'var(--logo-bg)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 22, fontWeight: 900, flexShrink: 0,
  },
  body: {
    background: 'var(--bg-card)',
    borderRadius: '0 0 12px 12px',
    padding: '22px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
  },
  input: {
    padding: '8px 12px', border: '1.5px solid var(--border-light)',
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-main)', fontFamily: 'inherit',
  },
  select: {
    padding: '8px 12px', border: '1.5px solid var(--border-light)',
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-main)', fontFamily: 'inherit', cursor: 'pointer',
  },
  btn: (primary) => ({
    padding: '8px 16px',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    background: primary ? 'var(--logo-bg)' : 'var(--bg-page)',
    color: primary ? '#fff' : 'var(--text-main)',
  }),
  statCard: {
    background: 'var(--stat-bg)',
    borderRadius: 10,
    padding: '14px 18px',
    textAlign: 'center',
    flex: 1,
    minWidth: 90,
  },
  empCard: {
    background: 'var(--bg-card)',
    border: '1.5px solid var(--border-light)',
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  badge: (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700,
    background: bg, color,
  }),
  modalOverlay: {
    position: 'fixed', inset: 0,
    background: 'var(--modal-overlay)',
    zIndex: 1000,
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '30px 16px',
    overflowY: 'auto',
  },
  modalBox: {
    background: 'var(--bg-card)',
    borderRadius: 14,
    border: '1px solid var(--border-light)',
    width: '100%',
    maxWidth: 680,
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    marginBottom: 30,
  },
  sectionHead: {
    fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    margin: '18px 0 8px',
  },
  infoRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', padding: '7px 0',
    borderBottom: '1px solid var(--border-divider)', fontSize: 13, gap: 12,
  },
};

// ─── Toggle Switch ───────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange }) => (
  <label
    style={{ position: 'relative', display: 'inline-flex', width: 44, height: 24, flexShrink: 0 }}
    onClick={e => e.stopPropagation()}
  >
    <input type="checkbox" checked={checked} onChange={onChange}
      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
    <span style={{
      position: 'absolute', inset: 0, borderRadius: 24,
      background: checked ? 'var(--toggle-on)' : 'var(--toggle-off)',
      cursor: 'pointer', transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', height: 18, width: 18,
        left: 3, bottom: 3, background: '#fff', borderRadius: '50%',
        transition: 'transform 0.2s',
        transform: checked ? 'translateX(20px)' : 'translateX(0)',
      }} />
    </span>
  </label>
);

// ─── Info Row ────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => {
  if (!value || value === 'Not Uploaded') return null;
  return (
    <div style={S.infoRow}>
      <span style={{ color: 'var(--text-secondary)', flexShrink: 0, marginRight: 8 }}>{label}</span>
      <span style={{ color: 'var(--text-main)', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

// ─── Chips ───────────────────────────────────────────────────────────────────

const Chips = ({ value }) => {
  if (!value) return null;
  const items = String(value).split(',').map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
      {items.map((item, i) => (
        <span key={i} style={{
          background: 'var(--stat-bg)', color: 'var(--text-main)',
          borderRadius: 6, padding: '2px 8px', fontSize: 11,
        }}>{item}</span>
      ))}
    </div>
  );
};

// ─── Modal Detail ────────────────────────────────────────────────────────────

const ModalDetail = ({ emp, onClose, onToggleActive }) => {
  const initials = getInitials(emp.fullName);
  const av = getAvatarColor(emp.fullName);
  const isActive = emp._active;
  const sb = statusBadge(isActive);
  const ab = approvalBadge(emp.finalStatus);

  const sections = [
    {
      title: '1. Personal Information',
      rows: [
        ['Full Name', emp.fullName], ['CNIC / ID', emp.cnic],
        ['Date of Birth', emp.dob], ['Nationality', emp.nationality],
        ['Mobile', emp.mobile], ['Email', emp.email],
        ['Permanent Address', emp.permanentAddress],
        ['Current Address', emp.currentAddress],
        ['Emergency Contact', emp.emergencyName],
        ['Emergency Number', emp.emergencyContact],
        ['Relation', emp.relation], ['Blood Group', emp.bloodGroup],
      ],
    },
    {
      title: '2. Documents',
      rows: [
        ['Photo', emp.photoFile], ['CNIC Front', emp.cnicFrontFile],
        ['CNIC Back', emp.cnicBackFile],
      ],
    },
    {
      title: '3. Job Source & References',
      rows: [
        ['Job Source', emp.jobSource], ['Other Source', emp.jobSourceOther],
        ['Reference 1', emp.ref1Name ? `${emp.ref1Name} — ${emp.ref1Contact}` : ''],
        ['Reference 2', emp.ref2Name ? `${emp.ref2Name} — ${emp.ref2Contact}` : ''],
      ],
    },
    {
      title: '4. Hiring & Documentation',
      rows: [
        ['Position Applied', emp.positionApplied],
        ['Interview Date', emp.interviewDate],
        ['Interviewed By', emp.interviewedBy],
        ['Hiring Decision', emp.hiringDecision],
        ['Mandatory Docs', emp.mandatoryDocs],
        ['Background Check', emp.backgroundVerification],
        ['Right to Work', emp.rightToWork],
      ],
    },
    {
      title: '5. Employment Details',
      rows: [
        ['Employee ID', emp.empId], ['Date of Joining', emp.doj],
        ['Employment Type', emp.employmentType],
        ['Department', emp.department], ['Designation', emp.designation],
        ['Reporting Manager', emp.reportingManager],
        ['Salary Structure', emp.salaryStructure],
        ['Probation End', emp.probationEnd],
      ],
    },
    {
      title: '6. iNOVAA System Access',
      rows: [
        ['Access Level', emp.accessLevel],
        ['iNOVAA Account', emp.novaAccount],
        ['GPS Tracking', emp.gpsTracking],
        ['Mobile Device', emp.mobileDeviceAssigned],
        ['Device ID', emp.deviceId],
        ['Profile Status', emp.profileStatus],
        ['Activation Date', emp.activationDate],
      ],
    },
    {
      title: '7. Assets Issued',
      rows: [
        ['Uniform', emp.uniform], ['PPE', emp.ppe],
        ['Equipment', emp.equipment],
        ['Asset Serials', emp.assetSerials],
        ['Issued By', emp.assetIssuedBy],
        ['Digital Signature', emp.digitalSignature],
      ],
    },
    {
      title: 'Final Approval',
      rows: [
        ['HR Approval', emp.hrApproval],
        ['Ops Approval', emp.opsApproval],
        ['Final Status', emp.finalStatus],
      ],
    },
  ];

  const isMultiVal = (val) => val && String(val).includes(',');

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>

        {/* Modal Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: av.bg, color: av.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-main)' }}>{emp.fullName || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{emp.designation || 'No Designation'} {emp.department ? `· ${emp.department}` : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {ab && <span style={S.badge(ab.bg, ab.color)}>{emp.finalStatus}</span>}
            <span style={S.badge(sb.bg, sb.color)}>{sb.label}</span>
            <Toggle checked={isActive} onChange={() => onToggleActive(emp)} />
            <button onClick={onClose} style={{ ...S.btn(false), padding: '6px 10px', fontSize: 16, borderRadius: '50%' }}>✕</button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '4px 24px 8px', maxHeight: '70vh', overflowY: 'auto' }}>
          {sections.map(sec => {
            const hasData = sec.rows.some(([, v]) => v && v !== 'Not Uploaded');
            if (!hasData) return null;
            return (
              <div key={sec.title}>
                <div style={S.sectionHead}>{sec.title}</div>
                {sec.rows.map(([label, val]) => {
                  if (!val || val === 'Not Uploaded') return null;
                  return (
                    <div key={label} style={S.infoRow}>
                      <span style={{ color: 'var(--text-secondary)', flexShrink: 0, marginRight: 8 }}>{label}</span>
                      {isMultiVal(val)
                        ? <Chips value={val} />
                        : <span style={{ color: 'var(--text-main)', fontWeight: 600, textAlign: 'right' }}>{val}</span>
                      }
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Employee Card ───────────────────────────────────────────────────────────

const EmpCard = ({ emp, onClick, onToggleActive }) => {
  const initials = getInitials(emp.fullName);
  const av = getAvatarColor(emp.fullName);
  const isActive = emp._active;
  const sb = statusBadge(isActive);
  const ab = approvalBadge(emp.finalStatus);

  return (
    <div style={S.empCard} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: av.bg, color: av.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.fullName || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.designation || 'No Designation'}</div>
        </div>
        {/* Active Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
          <span style={{ fontSize: 11, fontWeight: 600, color: isActive ? 'var(--badge-active-text)' : 'var(--text-secondary)' }}>{isActive ? 'Active' : 'Inactive'}</span>
          <Toggle checked={isActive} onChange={() => onToggleActive(emp)} />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <span style={S.badge(sb.bg, sb.color)}>● {sb.label}</span>
        {emp.department && <span style={S.badge('var(--stat-bg)', 'var(--text-main)')}>{String(emp.department).split(',')[0].trim()}</span>}
        {ab && emp.finalStatus && <span style={S.badge(ab.bg, ab.color)}>{emp.finalStatus}</span>}
      </div>

      <div style={{ borderTop: '1px solid var(--border-divider)', paddingTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
        {emp.mobile && <div style={{ color: 'var(--text-secondary)' }}>📱 {emp.mobile}</div>}
        {emp.doj && <div style={{ color: 'var(--text-secondary)' }}>📅 DOJ: {emp.doj}</div>}
        {emp.employmentType && <div style={{ color: 'var(--text-secondary)' }}>💼 {emp.employmentType}</div>}
        {emp.hiringDecision && <div style={{ color: 'var(--text-secondary)' }}>✅ {emp.hiringDecision}</div>}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right' }}>
        Click to view full profile →
      </div>
    </div>
  );
};

// ─── Main Dashboard ──────────────────────────────────────────────────────────

const NovaEmployeeDashboard = () => {
  // Add Observer so React is aware of Theme switches (useful for conditional renders if needed)
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
             document.body.classList.contains('dark') ||
             document.documentElement.getAttribute('data-theme') === 'dark' ||
             document.body.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode')
      );
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [allEmployees, setAllEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState(null);

  // Fetch from Google Sheets via Apps Script
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(SCRIPT_URL + '?action=getAll');
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        // Add _active field — default true if not already set
        const data = json.data.map(emp => ({
          ...emp,
          _active: emp._active !== undefined ? emp._active : (emp.profileStatus === 'Activated' || emp.finalStatus === 'Activated'),
        }));
        setAllEmployees(data);
        setEmployees(data);
      } else {
        throw new Error(json.message || 'No data returned');
      }
    } catch (err) {
      setError('Could not fetch data. Make sure your Google Apps Script is deployed and has a getAll action. Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter
  useEffect(() => {
    let list = [...allEmployees];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.fullName || '').toString().toLowerCase().includes(q) ||
        (e.designation || '').toString().toLowerCase().includes(q) ||
        (e.department || '').toString().toLowerCase().includes(q) ||
        (e.cnic || '').toString().includes(q)
      );
    }
    if (filterStatus === 'active') list = list.filter(e => e._active);
    if (filterStatus === 'inactive') list = list.filter(e => !e._active);
    setEmployees(list);
  }, [search, filterStatus, allEmployees]);

  const handleToggleActive = async (emp) => {
    const updated = allEmployees.map(e =>
      e === emp || (e.cnic && e.cnic === emp.cnic)
        ? { ...e, _active: !e._active }
        : e
    );
    setAllEmployees(updated);
    if (selected && selected.cnic === emp.cnic) {
      setSelected(prev => ({ ...prev, _active: !prev._active }));
    }

    // Optionally push active status back to Google Sheets
    try {
      await fetch(SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ action: 'updateStatus', cnic: emp.cnic, active: !emp._active }),
      });
    } catch (_) { /* fail silently */ }
  };

  // Stats
  const total = allEmployees.length;
  const activeCount = allEmployees.filter(e => e._active).length;
  const inactiveCount = total - activeCount;
  const approvedCount = allEmployees.filter(e => e.finalStatus === 'Activated' || e.hrApproval === 'Approved').length;

  return (
    <div className="nova-emp-wrapper" style={S.page}>
      <style>{themeStyles}</style>
      <div style={S.wrap}>

        {/* Header */}
        <div style={S.header}>
          <div style={S.logo}>i</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-main)' }}>iNOVAA Employee Dashboard</div>
            <div style={{ fontSize: 12, color: 'var(--badge-rejected-text)', fontWeight: 600, marginTop: 2 }}>SoWash Onboarding Records</div>
          </div>
        </div>

        {/* Body */}
        <div style={S.body}>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
            <input
              style={{ ...S.input, flex: 1, minWidth: 160 }}
              placeholder="Search by name, CNIC, designation..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select style={S.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Employees</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <button style={S.btn(true)} onClick={fetchData}>
              {loading ? '⏳ Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-main)' }}>{total}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Total Employees</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--badge-active-text)' }}>{activeCount}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Active</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-secondary)' }}>{inactiveCount}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Inactive</div>
            </div>
            <div style={S.statCard}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--badge-activated-text)' }}>{approvedCount}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Approved / Activated</div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: 'var(--badge-rejected-bg)', border: '1px solid var(--badge-rejected-text)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--badge-rejected-text)', marginBottom: 16 }}>
              ⚠️ {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-secondary)' }}>
              ⏳ Fetching employee records from Google Sheets...
            </div>
          )}

          {/* Empty */}
          {!loading && !error && employees.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-secondary)' }}>
              {allEmployees.length === 0
                ? 'No records found. Click Refresh to load data from Google Sheets.'
                : 'No employees match your search/filter.'}
            </div>
          )}

          {/* Grid */}
          {!loading && employees.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {employees.map((emp, i) => (
                <EmpCard
                  key={emp.cnic || i}
                  emp={emp}
                  onClick={() => setSelected(emp)}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <ModalDetail
          emp={selected}
          onClose={() => setSelected(null)}
          onToggleActive={handleToggleActive}
        />
      )}
    </div>
  );
};

export default NovaEmployeeDashboard;